import * as Progress from '@radix-ui/react-progress';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import StepImg from 'components/Step';
import CongratulationsDialog from 'components/dialog/CongratulationsDialog';
import useUserStore from 'store/user';
import useIncrementNumber from 'hooks/useIncrementNumber';
import useAudio from 'hooks/useAudio';
import useFlyingAnimation from 'hooks/useFlyingAnimation';

import PressBtn from 'assets/images/press_btn.png';
import CoinIcon from 'assets/images/coin_icon.png';
import SoundOnIcon from 'assets/images/sound_on_icon.svg';
import SoundOffIcon from 'assets/images/sound_off_icon.svg';
import PressedBtn from 'assets/images/pressed_btn.png';
import ChatBubble from 'assets/images/chat_bubble.png';
import { useAudioStore } from 'store/sound';
import type { CreateLedgerType, ResReadPointMainType, ResReadPointTargetType } from '@/types';
import { POINT_LEDGER_STATUS, SUCCESS } from '@/lib/name-space';
import { readMain, readTarget } from '@/api/balancer/read';
import useTargetStore, { type TargetState } from '@/store/target';
import { createLedger } from '@/api/balancer/create';
import Toast from '@/lib/toast';
import { useNavigate } from 'react-router-dom';
import { formatPoint } from '@/utils/format';
import { CountdownTimer } from '@/utils/CountdownTimer';
import NoticeDialog from '@/components/dialog/NoticeDialog';

type AcuireType = {
  possible: boolean;
  lastTime: string | null;
  setAmount: number | null;
  start: number;
};

const INTERVAL_MINUTES = 1000 * 60 * 1; // 1분
const MAX_INTERVAL_COUNT = 5; // 최대 인터벌 실행 횟수
const ACQUIRE_STATUS_EXPIRE_TIME = 10 * 60 * 1000; // 10분

export default function Play() {
  // store
  const { toggleMute, isPlaying } = useAudioStore();
  const { point, setPoint, ids, resetUser } = useUserStore();
  const { targetPoint, setTarget, notice, step, previousPoint, resetTarget } = useTargetStore();

  // hooks
  const { startAnimation } = useIncrementNumber();
  const { handlePlay: playButtonSound } = useAudio('/sounds/press_btn.wav');
  const { handlePlay: playPopupSound } = useAudio('/sounds/popup.wav');
  const { animationKey, buttonRef, isFlying, flyStyle, handleClick } = useFlyingAnimation();

  // state
  const [isPressed, setIsPressed] = useState(false);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [acquire, setAcquire] = useState<AcuireType>({
    possible: false,
    lastTime: null,
    setAmount: null,
    start: previousPoint,
  });
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [isPopupOpen] = useState(false);

  // refs
  const currentStep = useRef<number>(step);
  const intervarTimer = useRef<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();

  // 로컬스토리지 관련 함수들
  const checkAndCleanAcquireStatus = () => {
    const storedData = localStorage.getItem('acquireStatus');
    if (storedData) {
      const acquireData = JSON.parse(storedData);
      const currentTime = Date.now();

      if (currentTime > acquireData.expireTime) {
        localStorage.removeItem('acquireStatus');
        console.log('AcquireStatus expired and removed');
        return false;
      }

      const remainingTime = Math.ceil((acquireData.expireTime - currentTime) / 1000 / 60);
      console.log(`AcquireStatus valid, remaining: ${remainingTime} minutes`);

      return { isAcquire: acquireData.isAcquire, lastTime: acquireData.lastTime };
    }
    return false;
  };

  const decreaseLastTimeByOneMinute = () => {
    const storedData = localStorage.getItem('acquireStatus');
    if (storedData) {
      const acquireData = JSON.parse(storedData);

      if (acquireData.lastTime && typeof acquireData.lastTime === 'string') {
        const timeParts = acquireData.lastTime.split(':').map(Number);
        let hours = timeParts[0] || 0;
        let minutes = timeParts[1] || 0;
        const seconds = timeParts[2] || 0;

        if (minutes > 0) {
          minutes -= 1;
        } else if (hours > 0) {
          hours -= 1;
          minutes = 59;
        } else {
          localStorage.removeItem('acquireStatus');
          console.log('LastTime expired, removing acquireStatus');
          return;
        }

        const newLastTime = `${hours}:${minutes}:${seconds}`;
        const updatedAcquireData = {
          ...acquireData,
          lastTime: newLastTime,
        };
        localStorage.setItem('acquireStatus', JSON.stringify(updatedAcquireData));

        console.log(`LastTime decreased: ${acquireData.lastTime} → ${newLastTime}`);
        makeSetAcquire('lastTime', newLastTime);
      }
    }
  };

  const removeAcquireStatus = () => {
    if (localStorage.getItem('acquireStatus')) {
      localStorage.removeItem('acquireStatus');
      console.log('acquireStatus removed');
    }
  };

  const saveAcquireStatus = (isAcquire: boolean, lastTime: string) => {
    const acquireData = {
      isAcquire,
      lastTime,
      timestamp: Date.now(),
      expireTime: Date.now() + ACQUIRE_STATUS_EXPIRE_TIME,
    };
    localStorage.setItem('acquireStatus', JSON.stringify(acquireData));
  };

  // 상태 업데이트 함수들
  const makeSetAcquire = (key: string, value: string | number | boolean | null) => {
    setAcquire((prev) => ({ ...prev, [key]: value }));
  };

  const makeTarget = <K extends keyof TargetState>(key: K, value: TargetState[K]) => {
    setTarget(key, value);
  };

  // 데이터 초기화 함수들
  const initializeFromLocalStorage = (storedData: any) => {
    makeSetAcquire('possible', storedData.isAcquire);
    makeSetAcquire('lastTime', storedData.lastTime.toString());
    setIsInitialDataLoaded(true);
  };

  const initializeFromServerData = (data: ResReadPointMainType) => {
    if (point === 0) {
      setPoint(Number(data.total ?? 0));
    }

    makeTarget('season', data.season);
    makeSetAcquire('step', Number(data.step));
    makeTarget('step', Number(data.step));
    currentStep.current = Number(data.step);
    makeSetAcquire('start', Number(data.startAmount));

    const isPossible = data.acquireTime === null || data.isAcquire;
    makeSetAcquire('possible', isPossible);
    makeSetAcquire('lastTime', data.acquireTime);

    if (isPossible) {
      makeSetAcquire('setAmount', data.amount);
    }

    setIsInitialDataLoaded(true);
  };

  // 인터벌 데이터 업데이트 함수
  const fetchIntervalData = async () => {
    if (intervarTimer.current >= MAX_INTERVAL_COUNT) {
      setIsInitialDataLoaded(false);
      return;
    }

    const re = await readMain(ids);
    if (re.status !== SUCCESS) {
      console.log('error');
      return;
    }

    const data: ResReadPointMainType = re.data;
    if (!data.isAcquire) {
      setPoint(Number(data.total));
      makeSetAcquire('possible', data.isAcquire);
      makeSetAcquire('lastTime', data.acquireTime);
      removeAcquireStatus();
    }

    intervarTimer.current += 1;
  };

  // 초기 데이터 로드 함수
  const loadInitialData = async () => {
    if (!ids.pointId) {
      resetUser();
      resetTarget();
      navigate('/');
      return;
    }

    if (point === 0) {
      setPoint(0);
    }

    const re = await readMain(ids);
    if (re.status !== SUCCESS) {
      alert(`Data loading failed.`);
      resetUser();
      resetTarget();
      return;
    }

    const data: ResReadPointMainType = re.data;

    // 획득 가능할 때 로컬스토리지 체크
    if (data.isAcquire) {
      const storedAcquireStatus = checkAndCleanAcquireStatus();
      if (storedAcquireStatus) {
        initializeFromLocalStorage(storedAcquireStatus);
        return;
      }
    } else {
      removeAcquireStatus();
    }

    initializeFromServerData(data);
  };

  // useEffect들
  useEffect(() => {
    // 시즌 체크 - 시즌이 변경될 때 서버에서 tot 업데이트를 해줘야 한다.
    return () => {};
  }, []);

  // 인터벌 시간 후 잔액 업데이트
  useEffect(() => {
    if (!isInitialDataLoaded) {
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchIntervalData();
      const intervalId = setInterval(fetchIntervalData, INTERVAL_MINUTES);
      return () => clearInterval(intervalId);
    }, INTERVAL_MINUTES);

    return () => clearTimeout(timeoutId);
  }, [isInitialDataLoaded]);

  // 초기 데이터 로드
  useEffect(() => {
    loadInitialData();
  }, []);

  // 포인트 목표 달성 시 타겟 업데이트
  useEffect(() => {
    if (point >= targetPoint && ids.pointId) {
      (async () => {
        const re = await readTarget(ids, point.toString());
        if (re.status !== SUCCESS) {
          console.log('error');
          return;
        }

        const data: ResReadPointTargetType = re.data;
        makeTarget('targetPoint', Number(data.accPoint));
        makeTarget('title', data.title);
        makeTarget('previousPoint', Number(data.previousPoint));
        makeTarget('notice', data.describe);
        makeTarget('doneNotice', data.doneDescribe);
        makeSetAcquire('start', Number(data.previousPoint));
        makeTarget('maxStep', data.maxStep);

        if (data.count <= data.maxStep) {
          currentStep.current = data.count - 1;
          if (data.count < data.maxStep) {
            makeTarget('step', data.count);
          }
        }
      })();
    }
  }, [point, ids]);

  // 주기적으로 로컬스토리지 정리 (1분마다)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      if (!localStorage.getItem('acquireStatus')) {
        return;
      }
      decreaseLastTimeByOneMinute();
      checkAndCleanAcquireStatus();
    }, 60000);

    return () => clearInterval(cleanupInterval);
  }, [acquire.possible]);

  // 페이지 가시성 변경 시 로컬스토리지 정리
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkAndCleanAcquireStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [acquire.possible]);

  // 이벤트 핸들러들
  const handleClickButton = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (isFlying) return;

    const ledger: CreateLedgerType | null = makeLedger();
    if (!ledger) {
      setToastMsg('Unable to load');
      navigate('/');
      return;
    }
    if (!acquire.setAmount) {
      setToastMsg('Failed to load server');
      navigate('/');
      return;
    }

    makeSetAcquire('possible', false);

    const re = await createLedger(ledger, ids);
    saveAcquireStatus(re.data.isAcquire, re.data.remain);

    if (re.data.isAcquire) {
      makeSetAcquire('possible', re.data.isAcquire);
    }
    makeSetAcquire('lastTime', re.data.remain);
    setIsInitialDataLoaded(true);

    // UI 업데이트
    playButtonSound();
    setIsPressed(false);
    handleClick();
    startAnimation(Number(acquire.setAmount));

    // 축하 다이얼로그 체크
    timerRef.current = setTimeout(() => {
      if (point < 9000 && targetPoint - point <= 100) {
        playPopupSound();
        setIsOpenDialog(true);
      }
    }, 1000);
  };

  const handlePressBtn = (boolean: boolean) => {
    if (!acquire.possible) return;
    setIsPressed(boolean);
  };

  const makeLedger = (): CreateLedgerType | null => {
    if (acquire.setAmount === null || !ids.pointId) {
      return null;
    }
    return {
      amount: acquire.setAmount.toString(),
      status: POINT_LEDGER_STATUS.MINE,
    };
  };

  return (
    <>
      <div className="flex h-full flex-col items-center overflow-auto">
        <div className="w-full px-[18px] py-[20px]">
          <div className="mb-[9px] flex justify-between">
            <span className="text-[11px] text-[#FF4F19]">{formatPoint(acquire.start)}</span>
            <span className="text-[11px] text-[#FF4F19]">{formatPoint(targetPoint)}</span>
          </div>

          <Progress.Root
            className="relative h-[22px] w-full overflow-hidden rounded-full border-b-2 border-[#9CAAAE] bg-[#35160D]"
            style={{
              transform: 'translateZ(0)',
            }}
            max={100000}
            value={point}
          >
            <div className="border-3 size-full overflow-hidden rounded-full border-[#6C2D1A]">
              <Progress.Indicator
                className="size-full bg-white transition-all duration-[660ms] ease-linear"
                style={{
                  width: `${((point - acquire.start) / (targetPoint - acquire.start)) * 100}%`,
                  backgroundImage: 'linear-gradient(to bottom, #FFEEC4, #FF3D00)',
                }}
              />
            </div>
          </Progress.Root>

          <p className="mt-[10px] text-center text-[13px] font-bold text-[#6B2D19]">
            {/* Collect {acquire.end.toLocaleString()}P {notice} */}
            {notice}
          </p>
        </div>

        <div className="relative my-auto w-full px-[55px]">
          <StepImg step={currentStep.current} />
        </div>

        <div className="mt-auto w-full text-center">
          <div className="relative">
            {!acquire.possible && acquire.lastTime !== null && (
              <div className="absolute -top-[50px] left-1/2 z-20 -translate-x-1/2">
                <img src={ChatBubble} className="top-[10px] h-[55px] w-[163px] translate-y-[10px]" />
                <div className="absolute left-0 top-[18px] flex w-full flex-col gap-[6px] text-center">
                  <span className="h-[7px] text-[10px]">Get the next point!</span>
                  <span className="h-[5px] text-[7px]">
                    <CountdownTimer
                      remain={acquire.lastTime}
                      intervalType="minute"
                      onComplete={() => {
                        setTimeout(() => {
                          makeSetAcquire('possible', true);
                          makeSetAcquire('lastTime', null);
                        }, 0);
                      }}
                    />
                  </span>
                </div>
              </div>
            )}
            <button
              ref={buttonRef}
              className="relative mb-[30px]"
              onContextMenu={(e) => e.preventDefault()}
              onClick={handleClickButton}
              onMouseDown={() => handlePressBtn(true)}
              onMouseUp={() => handlePressBtn(false)}
              onMouseLeave={() => handlePressBtn(false)}
              onTouchStart={() => handlePressBtn(true)}
              onTouchEnd={() => handlePressBtn(false)}
              disabled={!acquire.possible}
            >
              <img src={isPressed ? PressedBtn : PressBtn} className="h-[94px] w-[96px]" />
              <AnimatePresence>
                {isFlying && (
                  <motion.div
                    key={animationKey}
                    className="fixed"
                    initial={{ opacity: 1, scale: 1 }}
                    animate={{
                      x: [flyStyle.left, flyStyle.midX, flyStyle.endLeft],
                      y: [flyStyle.top, flyStyle.midY, flyStyle.endTop],
                      rotate: [0, 180, 0],
                      scale: [1, 1.2, 0.8],
                      opacity: [1, 1, 0.8],
                      transition: { duration: 1, ease: 'linear' },
                    }}
                    exit={{ opacity: 0 }}
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      zIndex: 999,
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <img className="h-[35px] w-[30px]" src={CoinIcon} />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        <button className="absolute bottom-[96px] right-0 p-[6px]" onClick={toggleMute}>
          <img src={isPlaying ? SoundOnIcon : SoundOffIcon} />
        </button>
      </div>

      <CongratulationsDialog isOpenDialog={isOpenDialog} setIsOpenDialog={setIsOpenDialog} />
      {/* {showToast && (
        <Toast
          message={toastMsg}
          duration={3} // 초 단위
          onClose={() => setShowToast(false)}
        />
      )} */}
      <NoticeDialog open={isPopupOpen} />
    </>
  );
}
